import { removeKey } from "@prismicio/editor-support/Object"
import {
  useForceUpdate,
  useOnChange,
  useStableCallback
} from "@prismicio/editor-support/React"
import type { EditorOptions } from "@tiptap/core"
import { Editor } from "@tiptap/react"
import {
  type DependencyList,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"

// tiptap's useEditor does not render immediately on mount, creating a big flicker and editor layout shift.
// https://github.com/ueberdosis/tiptap/issues/2040
// In Tiptap 2.3.0, they made it "work" for server side rendering by creating the Editor instance in useEffect,
// but that breaks the first paint stability of the Editor: It renders with a height of 0, which is insane.
// This is a roughly similar hook as theirs but fixed for client side usage, without flickering and with a stable mounting height.
export function useEditor(
  options: Partial<UseEditorOptions> = {},
  deps: DependencyList = []
) {
  const onUpdate = useStableCallback(options.onUpdate)
  const onBlur = useStableCallback(options.onBlur)

  const isMounted = useRef(false)
  const forceUpdate = useForceUpdate()

  const [editor] = useState(() => ({ current: createEditor() }))

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableDeps = useMemo(() => deps, deps)

  useOnChange(stableDeps, () => {
    editor.current.destroy()
    editor.current = createEditor()
  })

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  })

  useDestroyEditorLater(editor.current, isMounted)

  function createEditor() {
    // Remove the callbacks from the initial props/options as they wouldn't remain fresh (stale closures)
    // Instead, use our own stable callbacks.
    const editor = new Editor(removeKey(options, "onUpdate", "onBlur"))

    editor.on("update", onUpdate)
    editor.on("blur", onBlur)

    editor.on("transaction", () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (isMounted.current) forceUpdate()
        })
      })
    })

    return editor
  }

  return editor.current
}

// Tiptap bugs out if you destroy an editor "synchronously" on each useEffect as it sometimes try to reuse the destroyed instance a bit later (often with a setTimeout)
// We don't really care when the instance is destroyed, we just want it destroyed at some point so it doesn't create a leak.
// https://github.com/ueberdosis/tiptap/issues/1451
// https://discuss.prosemirror.net/t/what-could-create-a-null-docview/1830
function useDestroyEditorLater(editor: Editor, isMounted: RefObject<boolean>) {
  useEffect(() => {
    return () => {
      window.setTimeout(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        if (isMounted.current) return
        if (editor.isDestroyed) return

        editor.destroy()
      }, 200)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor])
}

/**
 * setTimeout is required to combat tiptap flushsync issue
 * https://github.com/ueberdosis/tiptap/issues/3764
 */
export function handleEditorFlushSyncError(callback: VoidFunction) {
  return setTimeout(callback)
}

// Remove the closure options we don't officially support.
type UseEditorOptions = Omit<
  EditorOptions,
  | "onBeforeCreate"
  | "onCreate"
  | "onSelectionUpdate"
  | "onTransaction"
  | "onFocus"
  | "onDestroy"
>
